/**
 * Overwrite annotations for Premier change history.
 * When seamless sync / newest-chunk-wins discards a local (or remote) region,
 * we persist a readable summary + structured regions on ChapterChangeHistory.
 */

export type OverwriteRegion = {
  id: string;
  discardedText: string;
  keptText: string;
  /** Which side was kept after the policy ran. */
  winner: "local" | "remote";
};

export type OverwriteAnnotationPayload = {
  type: "overwrite_annotations";
  policy: "newest_chunk_wins" | "seamless_theirs" | "seamless_mine";
  regions: OverwriteRegion[];
};

export type ConflictHunkLike = {
  id: string;
  mineText: string;
  theirsText: string;
};

export type ChunkConflictLike = {
  id: string;
  localText: string;
  remoteText: string;
};

export type ChunkResolutionLike = {
  id: string;
  winner: "local" | "remote" | "equal";
};

function clip(text: string, max = 120): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** Build regions when seamless sync keeps all remote ("theirs") or all local ("mine"). */
export function regionsFromConflictHunks(
  hunks: ConflictHunkLike[],
  choice: "mine" | "theirs"
): OverwriteRegion[] {
  const regions: OverwriteRegion[] = [];
  for (const h of hunks) {
    const mine = h.mineText ?? "";
    const theirs = h.theirsText ?? "";
    if (mine === theirs) continue;
    if (choice === "theirs") {
      regions.push({
        id: h.id,
        discardedText: mine,
        keptText: theirs,
        winner: "remote",
      });
    } else {
      regions.push({
        id: h.id,
        discardedText: theirs,
        keptText: mine,
        winner: "local",
      });
    }
  }
  return regions;
}

/** Build regions from per-hunk mine/theirs choices (Premier seamless policy). */
export function regionsFromHunkResolutions(
  hunks: ConflictHunkLike[],
  resolutions: Array<{ hunkId: string; choice: "mine" | "theirs" | "combined" }>
): OverwriteRegion[] {
  const byId = new Map(resolutions.map((r) => [r.hunkId, r.choice]));
  const regions: OverwriteRegion[] = [];
  for (const h of hunks) {
    const choice = byId.get(h.id);
    if (!choice || choice === "combined") continue;
    const mine = h.mineText ?? "";
    const theirs = h.theirsText ?? "";
    if (mine === theirs) continue;
    if (choice === "theirs") {
      regions.push({
        id: h.id,
        discardedText: mine,
        keptText: theirs,
        winner: "remote",
      });
    } else {
      regions.push({
        id: h.id,
        discardedText: theirs,
        keptText: mine,
        winner: "local",
      });
    }
  }
  return regions;
}

/** Build regions from ChunkConflict + resolveNewestChunkWins results. */
export function regionsFromChunkResolutions(
  conflicts: ChunkConflictLike[],
  resolutions: ChunkResolutionLike[]
): OverwriteRegion[] {
  const byId = new Map(resolutions.map((r) => [r.id, r]));
  const regions: OverwriteRegion[] = [];
  for (const c of conflicts) {
    const r = byId.get(c.id);
    if (!r || r.winner === "equal") continue;
    if (r.winner === "local") {
      if (c.localText === c.remoteText) continue;
      regions.push({
        id: c.id,
        discardedText: c.remoteText,
        keptText: c.localText,
        winner: "local",
      });
    } else {
      regions.push({
        id: c.id,
        discardedText: c.localText,
        keptText: c.remoteText,
        winner: "remote",
      });
    }
  }
  return regions;
}

export function buildOverwriteAnnotationPayload(
  regions: OverwriteRegion[],
  policy: OverwriteAnnotationPayload["policy"]
): OverwriteAnnotationPayload | null {
  if (regions.length === 0) return null;
  return { type: "overwrite_annotations", policy, regions };
}

export function buildOverwriteSummary(regions: OverwriteRegion[]): string {
  if (regions.length === 0) return "Overwrite (no differing regions)";
  const n = regions.length;
  const first = regions[0]!;
  const side = first.winner === "remote" ? "Kept remote; discarded local" : "Kept local; discarded remote";
  const preview = clip(first.discardedText || first.keptText);
  if (n === 1) {
    return `${side}: “${preview || "(empty)"}”`;
  }
  return `${side} in ${n} regions. First: “${preview || "(empty)"}”`;
}

export function isOverwriteAnnotationPayload(value: unknown): value is OverwriteAnnotationPayload {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return v.type === "overwrite_annotations" && Array.isArray(v.regions);
}
